import type React from "react";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { useMetricLabel } from "./metricFormatters";
import getMetricSpec, { getFormatter, metricNameSelector } from "../../../modules/merchandising/utils/metrics";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";

export interface Metric {
  id?: number;
  type?: string;
  text?: string;
  query?: string;
  acos?: number;
  acos_delta?: number;
  spend?: number;
  spend_delta?: number;
  ad_spend?: number;
  ad_sales?: number;
  ad_sales_delta?: number;
  click_thru_rate?: number;
  click_thru_rate_delta?: number;
  conv_rate?: number;
  conv_rate_delta?: number;
  conversions?: number;
  cost?: number;
  cost_per_click?: number;
  cost_per_click_delta?: number;
  cpc?: number;
  serving_hours?: number;
  serving_hours_delta?: number;
  target_acos_max?: number;
  target_acos_min?: number;
  top_of_search_impression_share?: number;
  top_of_search_impression_share_delta?: number;
}

const MetricDisplay: React.FC<{ metric: Metric; showLabel?: boolean }> = ({ metric, showLabel = true }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["common", "metrics"]);
  const formatLabel = useMetricLabel();

  const renderMetricValue = (key: string, value: unknown) => {
    if (value === null || value === undefined) return null;

    const spec = getMetricSpec(key);
    const formattedValue = getFormatter(marketPlace, spec.format, spec)(value);
    const resolvedName = t(metricNameSelector(spec.id), { defaultValue: "" });
    const label = showLabel ? resolvedName || formatLabel(key) : null;

    return (
      <VStack key={key} gap="4px" alignItems="center">
        {label && <span className={css({ color: "#6B7280", fontSize: "12px", fontWeight: "500" })}>{label}</span>}
        <span className={css({ color: "#111827", fontSize: "14px", fontWeight: "600" })}>{formattedValue}</span>
      </VStack>
    );
  };

  const metricKeys = Object.keys(metric).filter(
    (key) => !["id", "type", "text", "query"].includes(key) && metric[key as keyof Metric] !== undefined,
  );

  return (
    <HStack gap="large" justifyContent="space-around" alignItems={"center"} width="100%" flexWrap="wrap">
      {metricKeys.map((key) => renderMetricValue(key, metric[key as keyof Metric]))}
    </HStack>
  );
};

export default MetricDisplay;
